/**
 * Saneamiento de la OI encriptada antes de que un evento salga hacia Sentry.
 *
 * El hash de `start/:hash` es la orden de inspección cifrada y se canjea por la inspección
 * completa sin autenticación: es una credencial portadora, no un identificador. Viaja por dos
 * formas distintas y hay que cubrir las dos, porque ninguna implica la otra:
 *
 *   - como segmento de ruta   `/start/<hash>`
 *   - como parámetro de query `?oi_encriptada=<hash>`  (inspection-api.service.ts:69,109)
 *
 * El SDK la recoge en `request.url` (httpcontext.js:19, que copia `location.href`), en el
 * `Referer` que arma con `document.referrer`, en el nombre de los spans de XHR
 * (`tracing/request.js:289`, que concatena la URL con query incluido), en el span de routing de
 * Angular —`sentry-angular.mjs:303`, que usa la URL cruda y que el parametrizador de `:311` nunca
 * corrige porque solo toca el span raíz— y en los breadcrumbs de navegación y de consola.
 *
 * Por eso el saneamiento es un recorrido profundo sobre el evento y no un parche campo por campo:
 * la lista de campos que hoy la contienen es más larga que la que cualquiera recuerda, y crece
 * con cada versión del SDK.
 */

export const REDACTED = '[redacted]';

/** Bajo este largo, un valor registrado es demasiado genérico para reemplazarlo sin colisionar. */
const MIN_SENSITIVE_LENGTH = 8;

/** Corta el recorrido en estructuras patológicas; los eventos reales no pasan de 6 o 7 niveles. */
const MAX_DEPTH = 12;

const ROUTE_HASH = /(\/start\/)([^/?#\s"'\\]+)/gi;
const QUERY_OI = /([?&]oi_encriptada=)([^&#\s"'\\]+)/gi;

const sensitiveValues = new Set<string>();

/**
 * Registra el valor concreto del hash para que también se limpie donde aparezca suelto, sin el
 * prefijo de ruta ni el de query. Las dos expresiones cubren las formas conocidas; esto cubre las
 * que no previmos.
 */
export function registerSensitiveValue(value: string | null | undefined): void {
  if (value && value.length >= MIN_SENSITIVE_LENGTH) {
    sensitiveValues.add(value);
  }
}

/**
 * Registra el hash tomándolo de la ruta apenas arranca la app, antes de que el SDK grabe su primer
 * breadcrumb. No alcanza con hacerlo en `StartPage`: entre el bootstrap y esa página hay una
 * ventana en la que un valor suelto —sin `/start/` ni `?oi_encriptada=` delante— no lo cubre
 * ninguna de las dos expresiones, y un breadcrumb ya grabado no se vuelve a revisar.
 */
export function registerSensitiveValuesFromLocation(pathname: string): void {
  const match = /\/start\/([^/?#]+)/.exec(pathname);
  if (match) {
    registerSensitiveValue(match[1]);
  }
}

/** Solo para las pruebas: devuelve el módulo a su estado inicial. */
export function resetSensitiveValues(): void {
  sensitiveValues.clear();
}

/**
 * Reemplaza el segmento salvo que ya esté saneado o sea el placeholder de la ruta parametrizada
 * (`/start/:hash/`), que es justamente la forma legible que queremos conservar.
 */
function replaceSegment(prefix: string, segment: string): string {
  if (segment === REDACTED || segment.startsWith(':')) {
    return prefix + segment;
  }
  return prefix + REDACTED;
}

export function scrubText(value: string): string {
  let scrubbed = value;
  for (const sensitive of sensitiveValues) {
    scrubbed = scrubbed.split(sensitive).join(REDACTED);
  }
  scrubbed = scrubbed.replace(ROUTE_HASH, (_match, prefix: string, segment: string) =>
    replaceSegment(prefix, segment),
  );
  return scrubbed.replace(QUERY_OI, (_match, prefix: string, segment: string) =>
    replaceSegment(prefix, segment),
  );
}

function scrubValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return scrubText(value);
  }
  if (value === null || typeof value !== 'object' || depth >= MAX_DEPTH) {
    return value;
  }
  if (seen.has(value as object)) {
    return value;
  }
  seen.add(value as object);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = scrubValue(value[i], depth + 1, seen);
    }
    return value;
  }
  for (const key of Object.keys(value as Record<string, unknown>)) {
    const container = value as Record<string, unknown>;
    container[key] = scrubValue(container[key], depth + 1, seen);
  }
  return value;
}

/**
 * Sanea el evento in situ y lo devuelve. Nunca descarta: un evento sin la credencial sigue siendo
 * la señal que 425 necesita medir.
 */
export function scrubEvent<T extends object>(event: T): T {
  return scrubValue(event, 0, new WeakSet<object>()) as T;
}

export function scrubBreadcrumb<T extends object>(breadcrumb: T): T {
  return scrubEvent(breadcrumb);
}
